import { toJss } from "@cirrus/legacy/app-common/alt/framework/skin/skinUtils.es6"
import withSkin from "@cirrus/legacy/app-common/alt/framework/withSkin.jsx"
import Icon from "@cirrus/legacy/app-components/icons/icon/index.jsx"
import Title from "@cirrus/legacy/app-components/titles/title/index.jsx"
import BrowserDetecter from "@cirrus/legacy/app-components/utils/browser-detecter.es6"
import controlElements from "@cirrus/legacy/app-components/utils/control-elements.es6"
import classNames from "classnames"
import ResizeSensor from "css-element-queries/src/ResizeSensor"
import eventlistener from "eventlistener"
import jss from "jss"
import keycode from "keycode"
import PerfectScrollbar from "perfect-scrollbar"
import PropTypes from "prop-types"
import React, { Component as C } from "react"

import styles from "./baseDialog.less"

export const baseDialogClassName = "baseDialogClassName"

class BaseDialog extends C {
  static dynamicStyles = null

  static defaultProps = {
    type: "base",
    autoFocus: true,
    hasInnerScrollX: true,
    hasInnerScrollY: true,
    hasOuterScrollX: false,
    hasOuterScrollY: false,
    closeButtonDisabled: false,
    hasPerfectScrollbar: true,
    headerIcon: "icon-cross2",
  }

  static propTypes = {
    type: PropTypes.string,

    actions: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.element), PropTypes.element]),

    autoFocus: PropTypes.bool,
    header: PropTypes.string,
    hasInnerScrollX: PropTypes.bool,
    hasInnerScrollY: PropTypes.bool,
    hasOuterScrollX: PropTypes.bool,
    hasOuterScrollY: PropTypes.bool,
    hideCloseButton: PropTypes.bool,
    closeButtonDisabled: PropTypes.bool,
    hideFooter: PropTypes.bool,
    hideHeader: PropTypes.bool,
    minHeight: PropTypes.number,
    minWidth: PropTypes.number,
    footerClassName: PropTypes.string,
    headerContent: PropTypes.any,
    footerContent: PropTypes.any,
    headerIcon: PropTypes.string,
    aboveHeaderContent: PropTypes.any,
    allowPerfectScrollbarForIE: PropTypes.bool,
    hasPerfectScrollbar: PropTypes.bool,
  }

  perfectScrollbar = null

  constructor(props) {
    super(props)

    this._closeButtonTabIndex = 10100
    this._previousProps = {}
    this.updateScrollbar = this.updateScrollbar.bind(this)
    this.handleKeyDown = this.handleKeyDown.bind(this)

    if (BaseDialog.dynamicStyles == null) {
      const dynamicStylesConfig = toJss(props.skinObject.dialog || {}, {
        content: { color: "" },
        main: { color: "" },
        header: { color: "" },
        footer: { color: "" },
      })

      BaseDialog.dynamicStyles = jss.createStyleSheet(dynamicStylesConfig).attach()
    }
  }

  componentDidMount() {
    this.initScrollbar()
    this.focusFirstControl(this.props.autoFocus)
    this.manageHandlers(true)

    Object.assign(this._previousProps, this.props)
  }

  componentWillUnmount() {
    this.manageHandlers(false)
    this._resizeSensor && this._resizeSensor.detach()
  }

  manageHandlers(add) {
    const action = add ? "add" : "remove"

    eventlistener[action](window, "resize", this.updateScrollbar)
    eventlistener[action](window, "keydown", this.handleKeyDown)
  }

  handleKeyDown(e) {
    if (keycode(e) === "esc" && !this.props.closeButtonDisabled) {
      this.props.onRequestClose()
    }
  }

  focusFirstControl(shouldFocus) {
    if (!shouldFocus) return

    const firstControl = this.refs.modalBody.querySelectorAll(controlElements.join(", "))[0]

    if (firstControl) firstControl.focus()
  }

  initScrollbar() {
    if (!this.hasPerfectScrollbar()) {
      return
    }

    this.refs.modalBody.style.overflow = "hidden"
    this.perfectScrollbar = new PerfectScrollbar(this.refs.modalBody, {
      wheelPropagation: false,
    })

    this._resizeSensor = new ResizeSensor(this.refs.modalScrolledArea, this.updateScrollbar)
  }

  updateScrollbar() {
    if (!this.hasPerfectScrollbar()) {
      return
    }

    const { modalBody } = this.refs

    if (!modalBody) {
      return
    }

    this.perfectScrollbar && this.perfectScrollbar.update()
  }

  hasPerfectScrollbar() {
    const isIEorEDGE = Boolean(BrowserDetecter.detectEdgeOrIE())

    if (!this.props.hasPerfectScrollbar) {
      return false
    }

    if (isIEorEDGE && !this.props.allowPerfectScrollbarForIE) {
      return false
    }

    return true
  }

  isIEorEDGE() {
    const version = BrowserDetecter.detectEdgeOrIE()

    return version !== false
  }

  scrollToTop() {
    const { modalBody } = this.refs

    if (modalBody) {
      modalBody.scrollTop = 0
      this.hasPerfectScrollbar() && this.perfectScrollbar && this.perfectScrollbar.update()
    }
  }

  getScrollbarOptions() {
    return {
      scrollXMarginOffset: 10,
      scrollYMarginOffset: 10,
      suppressScrollX: !this.props.hasInnerScrollX,
      suppressScrollY: !this.props.hasInnerScrollY,
    }
  }

  render() {
    const { classes } = BaseDialog.dynamicStyles

    let { actions } = this.props

    if (actions && actions.map) {
      actions = actions.map((action, index) => (
        <span className={styles.footerAction} key={index}>
          {action}
        </span>
      ))
    }

    const contentClassName = classNames(
      styles.content,
      classes.content,
      styles[this.props.type],
      styles.dragContent,
      this.props.contentClassName,
      styles.noCursor,
    )

    const mainClassName = classNames(styles.main, classes.main, this.props.mainClassName)

    const closeButtonClassName = classNames(styles.closeIcon, this.props.closeButtonClassName)

    return (
      <div
        className={contentClassName}
        aria-labelledby="modal-header"
        aria-describedby="modal-content"
        aria-modal="true"
        role="alertdialog"
        style={{
          minWidth: this.props.minWidth,
          marginTop: this.props.top,
          width: this.props.contentWidth,
        }}>
        {this.props.hideHeader ? null : (
          <header
            onDoubleClick={this.props.onHeaderDoubleClick}
            id="modal-header"
            role="heading"
            className={classNames(
              baseDialogClassName,
              styles.header,
              classes.header,
              styles.cursor,
              this.props.headerClassName,
            )}>
            {this.props.header && <Title className={styles.title} title={this.props.header} />}

            {this.props.headerContent}

            {this.props.hideCloseButton ? null : (
              <button
                className={closeButtonClassName}
                tabIndex={this._closeButtonTabIndex}
                onClick={this.props.onRequestClose}
                disabled={this.props.closeButtonDisabled}
                aria-hidden="true">
                <Icon
                  icon={this.props.headerIcon}
                  className={classNames(styles.icon, styles.iconCross3)}
                />
              </button>
            )}
          </header>
        )}

        {this.props.aboveHeaderContent ? this.props.aboveHeaderContent : null}

        <main
          ref="modalBody"
          className={mainClassName}
          style={{ minHeight: this.props.minHeight }}
          role="document"
          id="modal-content">
          <div className={styles.headerElement}>{this.props.headerElement}</div>

          <div
            ref="modalScrolledArea"
            className={classNames(styles.mainContent, this.props.mainContentClassName)}>
            {this.props.children}
          </div>
        </main>

        {this.props.footerContent ? this.props.footerContent : null}

        {this.props.hideFooter || this.props.footerContent ? null : (
          <div className={classNames(styles.footer, classes.footer, this.props.footerClassName)}>
            {actions}
          </div>
        )}
      </div>
    )
  }
}

export default withSkin(BaseDialog)
